/**
 * Stage task checklist (P3) — the Phase-Gate checklist for one plan stage,
 * grouped by sub-stage. Each task can be completed / skipped; DATE tasks record
 * a date, QC_FORM tasks capture received/damaged counts, and tasks that require
 * evidence take a photo/file. PAYMENT tasks are read-only (driven by the
 * installment). Advancing past a sub-stage needs its mandatory tasks done.
 */
import React, { useState } from "react";
import { toast } from "react-toastify";
import { Check, Circle, SkipForward, X, Paperclip, Lock } from "lucide-react";
import { OutlineButton, PrimaryButton } from "../../components/Buttons";
import { updateExecutionTask } from "../../services/interiorExecution";

const todayISO = () => new Date().toISOString().slice(0, 10);

const STATUS_PILL = {
  PENDING: "bg-slate-100 text-slate-500",
  DONE: "bg-emerald-50 text-emerald-700",
  SKIPPED: "bg-amber-50 text-amber-600",
  BLOCKED: "bg-rose-50 text-rose-600",
};

const SUBSTAGE_ORDER = ["PLANNING", "PROCUREMENT", "MANUFACTURING", "DELIVERY", "EXECUTION", "QC", "CLOSURE"];

const StageTasksChecklist = ({ tasks, currentSubStage, onChanged, disabled, onlySubStage = null }) => {
  const [actionTask, setActionTask] = useState(null); // task being completed via the modal
  const [form, setForm] = useState({ recordedDate: todayISO(), received: "", damaged: "", notes: "", file: null });
  const [busy, setBusy] = useState(false);

  if (!tasks || tasks.length === 0) return null;

  const bySub = {};
  tasks.forEach((t) => { (bySub[t.subStage] = bySub[t.subStage] || []).push(t); });
  // onlySubStage: render just one sub-stage's tasks (used by the current-step zone).
  const subs = (onlySubStage ? [onlySubStage] : SUBSTAGE_ORDER).filter((s) => bySub[s]);

  if (subs.length === 0) return null;

  const openComplete = (t) => {
    setActionTask(t);
    setForm({ recordedDate: todayISO(), received: "", damaged: "", notes: "", file: null });
  };

  const quickSkip = async (t) => {
    setBusy(true);
    try {
      await updateExecutionTask(t.executionTaskId, { status: "SKIPPED" });
      toast.success(`${t.label} skipped`);
      if (onChanged) await onChanged();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to skip task");
    } finally { setBusy(false); }
  };

  const submitComplete = async () => {
    const t = actionTask;
    if (t.taskKind === "QC_FORM" && form.received === "")
      return toast.error("Enter the received quantity");
    if (t.requiresEvidence && !t.evidenceUrl && !form.file)
      return toast.error("This task needs an evidence upload");
    setBusy(true);
    try {
      const fields = { status: "DONE", notes: form.notes || null };
      if (t.taskKind === "DATE") fields.recordedDate = form.recordedDate;
      if (t.taskKind === "QC_FORM")
        fields.metadataJson = { received: Number(form.received), damaged: Number(form.damaged || 0) };
      await updateExecutionTask(t.executionTaskId, fields, form.file);
      toast.success(`${t.label} done`);
      setActionTask(null);
      if (onChanged) await onChanged();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to complete task");
    } finally { setBusy(false); }
  };

  return (
    <div className="mt-3 border-t border-slate-100 pt-2 space-y-3">
      {subs.map((sub) => (
        <div key={sub}>
          <p className={`text-[10px] font-bold uppercase tracking-wide mb-1 ${sub === currentSubStage ? "text-flamingo-600" : "text-slate-400"}`}>
            {sub}{sub === currentSubStage ? " · current" : ""}
          </p>
          <div className="space-y-1">
            {bySub[sub].map((t) => {
              const done = t.status === "DONE";
              const skipped = t.status === "SKIPPED";
              const isPayment = t.taskKind === "PAYMENT";
              return (
                <div key={t.executionTaskId} className="flex items-center gap-2 text-xs">
                  {done ? <Check size={14} className="text-emerald-500 shrink-0" />
                    : skipped ? <SkipForward size={14} className="text-amber-500 shrink-0" />
                    : <Circle size={14} className="text-slate-300 shrink-0" />}
                  <span className={`flex-1 ${done ? "text-slate-500 line-through" : "text-slate-700"}`}>
                    {t.label}
                    {t.isMandatory && <span className="text-rose-400 ml-0.5" title="Mandatory">*</span>}
                    {t.recordedDate && <span className="text-slate-400 ml-1.5">{t.recordedDate}</span>}
                    {t.evidenceUrl && (
                      <button onClick={() => window.open(t.evidenceUrl, "_blank", "noopener")}
                        className="inline-flex items-center gap-0.5 text-sky-600 hover:underline ml-1.5"><Paperclip size={10} /> proof</button>
                    )}
                  </span>
                  <span className={`px-1.5 py-0.5 rounded text-[9px] font-semibold ${STATUS_PILL[t.status]}`}>{t.status}</span>
                  {isPayment ? (
                    <Lock size={11} className="text-slate-300" title="Completed by recording the installment" />
                  ) : !done && !skipped && !disabled ? (
                    <span className="inline-flex gap-1">
                      <button onClick={() => openComplete(t)} disabled={busy}
                        className="text-[10px] font-semibold text-emerald-600 hover:underline">Done</button>
                      {!t.isMandatory && (
                        <button onClick={() => quickSkip(t)} disabled={busy}
                          className="text-[10px] font-semibold text-slate-400 hover:underline">Skip</button>
                      )}
                    </span>
                  ) : null}
                </div>
              );
            })}
          </div>
        </div>
      ))}

      {/* complete modal — date / QC form / evidence as the task requires */}
      {actionTask && (
        <div className="fixed inset-0 bg-slate-900/50 flex items-center justify-center z-50">
          <div className="bg-white rounded-2xl shadow-xl w-full max-w-sm">
            <div className="flex items-center justify-between px-5 py-4 border-b border-slate-100">
              <h3 className="text-sm font-semibold text-slate-900">{actionTask.label}</h3>
              <button onClick={() => setActionTask(null)} className="text-slate-400 hover:text-slate-600"><X size={18} /></button>
            </div>
            <div className="p-5 space-y-3">
              {actionTask.taskKind === "DATE" && (
                <div>
                  <label className="text-xs font-medium text-slate-600">Date</label>
                  <input type="date" value={form.recordedDate} max={todayISO()}
                    onChange={(e) => setForm({ ...form, recordedDate: e.target.value })}
                    className="mt-1 w-full rounded-xl border border-slate-300 py-2 px-3 text-sm" />
                </div>
              )}
              {actionTask.taskKind === "QC_FORM" && (
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="text-xs font-medium text-slate-600">Qty received *</label>
                    <input type="number" min="0" value={form.received}
                      onChange={(e) => setForm({ ...form, received: e.target.value })}
                      className="mt-1 w-full rounded-xl border border-slate-300 py-2 px-3 text-sm" />
                  </div>
                  <div>
                    <label className="text-xs font-medium text-slate-600">Qty damaged</label>
                    <input type="number" min="0" value={form.damaged}
                      onChange={(e) => setForm({ ...form, damaged: e.target.value })}
                      className="mt-1 w-full rounded-xl border border-slate-300 py-2 px-3 text-sm" />
                  </div>
                </div>
              )}
              {actionTask.requiresEvidence && (
                <div>
                  <label className="text-xs font-medium text-slate-600">Evidence {actionTask.evidenceUrl ? "(replace)" : "*"}</label>
                  <input type="file" accept="image/*,application/pdf"
                    onChange={(e) => setForm({ ...form, file: e.target.files?.[0] || null })}
                    className="mt-1 block w-full text-xs text-slate-600 file:mr-2 file:rounded-lg file:border-0 file:bg-slate-100 file:px-3 file:py-1.5 file:text-slate-700" />
                </div>
              )}
              <div>
                <label className="text-xs font-medium text-slate-600">Notes</label>
                <input value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })}
                  placeholder="(optional)" className="mt-1 w-full rounded-xl border border-slate-300 py-2 px-3 text-sm" />
              </div>
            </div>
            <div className="flex justify-end gap-2 px-5 py-4 bg-slate-50 border-t border-slate-200">
              <OutlineButton className="px-4 py-2 text-sm" onClick={() => setActionTask(null)}>Cancel</OutlineButton>
              <PrimaryButton className="px-5 py-2 text-sm" onClick={submitComplete} disabled={busy}>
                {busy ? "Saving…" : "Mark Done"}
              </PrimaryButton>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default StageTasksChecklist;
